// src/components/Popups/ReservationEditPopup.jsx
import React, {
   useEffect,
   useMemo,
   useState,
   useCallback,
   useRef,
   useContext,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ro from "date-fns/locale/ro";
import UIIcon from "../../Common/UIIcon";
import IconButton from "../../Common/IconButton";

import { fetchStudents } from "../../../store/studentsSlice";
import { fetchInstructors } from "../../../store/instructorsSlice";
import {
   removeReservation,
   fetchReservationsDelta,
   fetchUserReservations,
   patchReservationLocal,
} from "../../../store/reservationsSlice";
import {
   createReservationsForUser,
   getReservationHistory,
   patchReservation,
   restoreReservation,
} from "../../../api/reservationsService";

import { triggerCalendarRefresh } from "../../Utils/calendarBus";
import { getCalendarStore } from "../../../calendar/master/store/calendarStoreRegistry";
import { applyReservationGridPatch } from "../../../calendar/interaction/applyReservationGridPatch";
import {
   closePopup as closePopupStore,
   closeSubPopup as closeSubPopupStore,
} from "../../Common/popupUIStore";

import AlertPills from "../../Utils/AlertPills";
import { UserContext } from "../../../UserContext";
import { canOwnCalendarReservations } from "../../../constants/instructors";
import { copyTextToClipboardSafe } from "../../APanel/CalendarPlus/utils";
import {
   MOLDOVA_TZ,
   BUSY_KEYS_MODE,
   getFmt,
   localDateStrTZ,
   tzOffsetMinutesAt,
   toUtcIsoFromMoldova,
   isoForDbMatchLocalHour,
   localKeyFromTs,
   localKeyForIso,
   localKeyForDateAndTime,
   busyLocalKeyFromStored,
   readRawDateTimeParts,
} from "../../../utils/moldovaTime";
import {
   CALENDAR_EVENT_COLOR_GRID_COLUMNS,
   CALENDAR_EVENT_COLOR_HINTS,
   CALENDAR_EVENT_COLOR_LABELS,
   CALENDAR_EVENT_COLOR_TOKENS,
   CALENDAR_EVENT_DEFAULT_COLOR_TOKEN,
   calendarEventColorSwatchStyle,
   normalizeCalendarEventColorToken,
} from "../../../constants/calendarEventColors";

import {
   isSameStoredStart,
   resolveStartTimeForCancellationTransition,
} from "./cancellationStartTime";
import { LESSON_SLOT_OPTIONS } from "../../../constants/lessonSlots";
import { EditReservationContext } from "./EditReservationContext";
import ReservationHistoryTab from "./ReservationHistoryTab";
import ReservationStudentSearch from "./ReservationStudentSearch";
import ReservationInstructorSearch from "./ReservationInstructorSearch";
import useDistricts from "../../../hooks/useDistricts";
import { activeDistrictNames, districtKey } from "../../../utils/districts";

/* ===== Locale RO ===== */
registerLocale("ro", ro);

const SEARCH_RESULTS_LIMIT = 10;
const EMPTY_KEY_SET = new Set();
const EMPTY_RESERVATIONS = [];
const EMPTY_USERS = [];
const AVAILABILITY_CACHE_MAX = 80;
const DEFAULT_AVAILABILITY_DAYS = 60;
const ADMIN_MANAGER_AVAILABILITY_DAYS = 731;
const ADMIN_MANAGER_AVAILABILITY_BACK_OFFSET_DAYS = 365;
const AVAILABILITY_BY_RESERVATIONS_REF = new WeakMap();
const BUSY_LOCAL_KEY_CACHE = new Map();
const USER_BY_ID_CACHE = new WeakMap();
const RESERVATION_BY_ID_CACHE = new WeakMap();

function getCachedBusyLocalKey(startValue) {
   const raw = String(startValue ?? "");
   if (!raw) return "";

   const cached = BUSY_LOCAL_KEY_CACHE.get(raw);
   if (cached != null) return cached;

   const next = busyLocalKeyFromStored(raw);
   BUSY_LOCAL_KEY_CACHE.set(raw, next);
   return next;
}

function getUserByIdMap(usersList) {
   if (!Array.isArray(usersList)) return new Map();
   const cached = USER_BY_ID_CACHE.get(usersList);
   if (cached) return cached;

   const map = new Map();
   for (const user of usersList) {
      const id = user?.id;
      if (id == null) continue;
      map.set(String(id), user);
   }

   USER_BY_ID_CACHE.set(usersList, map);
   return map;
}

function getReservationEntityId(row) {
   if (!row || typeof row !== "object") return null;
   return (
      row.id ??
      row._id ??
      row.reservationId ??
      row.reservation_id ??
      row.reservation?.id ??
      null
   );
}

function getReservationById(reservationsList, reservationId) {
   if (!Array.isArray(reservationsList)) return null;
   const targetId = String(reservationId ?? "");
   if (!targetId) return null;

   let byId = RESERVATION_BY_ID_CACHE.get(reservationsList);
   if (!byId) {
      byId = new Map();
      for (const r of reservationsList) {
         const id = getReservationEntityId(r);
         if (id == null) continue;
         byId.set(String(id), r);
      }
      RESERVATION_BY_ID_CACHE.set(reservationsList, byId);
   }
   return byId.get(targetId) || null;
}

function mergeReservationRows(...rows) {
   let merged = null;

   for (const row of rows) {
      if (!row || typeof row !== "object") continue;

      merged = merged ? { ...merged, ...row } : { ...row };

      if (merged?.reservation || row?.reservation) {
         merged.reservation = {
            ...(merged?.reservation || {}),
            ...(row?.reservation || {}),
         };
      }

      if (merged?.instructor || row?.instructor) {
         merged.instructor = {
            ...(merged?.instructor || {}),
            ...(row?.instructor || {}),
         };
      }

      if (merged?.student || row?.student) {
         merged.student = {
            ...(merged?.student || {}),
            ...(row?.student || {}),
         };
      }

      if (merged?.user || row?.user) {
         merged.user = {
            ...(merged?.user || {}),
            ...(row?.user || {}),
         };
      }
   }

   return merged;
}

function normalizeInstructorIdStrict(value) {
   if (value == null) return null;
   const num = Number(String(value).trim());
   if (!Number.isFinite(num) || num <= 0) return null;
   return Math.trunc(num);
}

function readPairAvailabilityCache(reservationsRef, pairKey) {
   if (!reservationsRef || !pairKey) return null;
   const byPair = AVAILABILITY_BY_RESERVATIONS_REF.get(reservationsRef);
   if (!byPair) return null;
   return byPair.get(pairKey) || null;
}

function writePairAvailabilityCache(
   reservationsRef,
   pairKey,
   studentBusySet,
   instructorBusySet,
) {
   if (!reservationsRef || !pairKey) return;
   let byPair = AVAILABILITY_BY_RESERVATIONS_REF.get(reservationsRef);
   if (!byPair) {
      byPair = new Map();
      AVAILABILITY_BY_RESERVATIONS_REF.set(reservationsRef, byPair);
   }
   if (byPair.size >= AVAILABILITY_CACHE_MAX) {
      const firstKey = byPair.keys().next().value;
      if (firstKey != null) byPair.delete(firstKey);
   }
   byPair.set(pairKey, {
      studentBusySet,
      instructorBusySet,
   });
}

const nowHHMMInMoldova = () =>
   getFmt("en-GB", MOLDOVA_TZ, "time").format(new Date());

/* ===== Intervale ore ===== */
const oreDisponibile = LESSON_SLOT_OPTIONS;

/* Culori — paletă comună ultra / examen practic (10 + 10 = 20) */
const COLOR_TOKENS = CALENDAR_EVENT_COLOR_TOKENS;
const COLOR_LABEL = CALENDAR_EVENT_COLOR_LABELS;
const COLOR_HINTS = CALENDAR_EVENT_COLOR_HINTS;
const COLOR_GRID_STYLE = {
   gridTemplateColumns: `repeat(${CALENDAR_EVENT_COLOR_GRID_COLUMNS}, minmax(0, 1fr))`,
};

const normalizeColorToken = (value, fallback = CALENDAR_EVENT_DEFAULT_COLOR_TOKEN) =>
   normalizeCalendarEventColorToken(value, fallback);

const fmtDateTimeRO = (iso) => {
   const raw = readRawDateTimeParts(iso);
   if (raw) return `${raw.day}.${raw.month}.${raw.year}, ${raw.hour}:${raw.minute}`;
   return new Date(iso).toLocaleString("ro-RO", {
      dateStyle: "medium",
      timeStyle: "short",
   });
};

const fmtDateRO = (iso) => {
   if (!iso) return "—";
   const raw = readRawDateTimeParts(iso);
   if (raw) return `${raw.day}.${raw.month}.${raw.year}`;
   const date = new Date(iso);
   if (Number.isNaN(date.getTime())) return "—";
   return date.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
   });
};

const fmtTimeRO = (iso) => {
   if (!iso) return "—";
   const raw = readRawDateTimeParts(iso);
   if (raw) return `${raw.hour}:${raw.minute}`;
   const date = new Date(iso);
   if (Number.isNaN(date.getTime())) return "—";
   return date.toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
   });
};

const localDateStr = (d) => localDateStrTZ(d, MOLDOVA_TZ);

const getStartFromReservation = (r) =>
   r?.startTime ??
   r?.start ??
   r?.start_time ??
   r?.dateTime ??
   r?.datetime ??
   r?.date ??
   r?.begin ??
   null;

const localDateObjFromStr = (s) => {
   const [y, m, d] = s.split("-").map(Number);
   return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
};

const nextNDays = (n, fromDate = new Date()) => {
   const out = [];
   const base = new Date(fromDate);
   base.setHours(0, 0, 0, 0);
   for (let i = 0; i < n; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      out.push(localDateStr(d));
   }
   return out;
};

const buildFullGridSlots = (
   daysWindow = DEFAULT_AVAILABILITY_DAYS,
   startFrom,
) => {
   const daysArr = nextNDays(daysWindow, startFrom ?? new Date());
   const out = [];
   for (const dayStr of daysArr) {
      const dObj = localDateObjFromStr(dayStr);
      for (const t of oreDisponibile) {
         const iso = toUtcIsoFromMoldova(dObj, t.oraStart);
         out.push({
            iso,
            key: localKeyForIso(iso),
         });
      }
   }
   return out;
};

function buildFreeSlotsFromBusySets(fullGrid, studentSet, instructorSet) {
   const out = [];
   for (const slot of fullGrid || []) {
      const key = slot?.key;
      if (!key) continue;
      if (studentSet.has(key) || instructorSet.has(key)) continue;
      out.push(slot.iso);
   }
   return out;
}

function isAdminOrManager(userLike) {
   const role = String(
      userLike?.role ??
         userLike?.Role ??
         userLike?.userRole ??
         userLike?.type ??
         userLike?.profile?.role ??
         "",
   ).toUpperCase();
   return role === "ADMIN" || role === "MANAGER";
}

const hasInstructorConflict = (
   reservations,
   instructorId,
   isoStart,
   excludeReservationId,
) => {
   if (!instructorId || !isoStart) return false;
   const key = localKeyForIso(isoStart);
   return (reservations || [])
      .filter(
         (r) =>
            String(getReservationEntityId(r) ?? "") !==
            String(excludeReservationId ?? ""),
      )
      .filter((r) => String(r?.instructorId ?? "") === String(instructorId))
      .some((r) => {
         const st = getStartFromReservation(r);
         if (!st) return false;
         const rKey = getCachedBusyLocalKey(st);
         return rKey === key;
      });
};

const hasStudentConflict = (
   reservations,
   studentId,
   isoStart,
   excludeReservationId,
) => {
   if (!studentId || !isoStart) return false;
   const key = localKeyForIso(isoStart);
   return (reservations || [])
      .filter(
         (r) =>
            String(getReservationEntityId(r) ?? "") !==
            String(excludeReservationId ?? ""),
      )
      .filter(
         (r) => String(r?.userId ?? r?.studentId ?? "") === String(studentId),
      )
      .some((r) => {
         const st = getStartFromReservation(r);
         if (!st) return false;
         const rKey = getCachedBusyLocalKey(st);
         return rKey === key;
      });
};

function escapeRegExp(value) {
   return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightText(text, query) {
   if (!text) return "";
   if (!query) return text;
   // Fără escape, un „+" tastat în căutare (numerele moldovenești încep cu
   // +373) face RegExp să arunce SyntaxError chiar în timpul randării.
   const safe = escapeRegExp(query);
   if (!safe) return text;
   const parts = text.toString().split(new RegExp(`(${safe})`, "gi"));
   return parts.map((part, i) =>
      part.toLowerCase() === (query || "").toLowerCase() ? (
         <i key={i} className="highlight">
            {part}
         </i>
      ) : (
         part
      ),
   );
}

/* ===== Istoric: păstrăm câmpurile exact cum vin din API ===== */
const historyRawValueToString = (value) => {
   if (value === undefined) return "";
   if (value === null) return "null";
   if (typeof value === "string") return value;
   if (
      typeof value === "number" ||
      typeof value === "boolean" ||
      typeof value === "bigint"
   ) {
      return String(value);
   }
   try {
      return JSON.stringify(value);
   } catch {
      return String(value);
   }
};

const personName = (person) =>
   `${person?.firstName || ""} ${person?.lastName || ""}`.trim();

const colorNameFromValue = (value) => {
   const token = normalizeColorToken(value, "");
   if (!token) return "";
   const normalized = token.replace(/^--/, "").replace(/^event-/, "");
   return COLOR_LABEL[normalized] || token;
};

const reservationColorToCss = (value) => {
   const raw = String(value ?? "").trim();
   if (!raw) return "var(--event-default)";
   if (raw.startsWith("var(")) return raw;
   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      if (!short || short === "default") return "var(--event-default)";
      if (short.startsWith("event-")) return `var(--${short})`;
      return `var(--event-${short})`;
   }
   if (/^event-/i.test(raw)) return `var(--${raw.toLowerCase()})`;
   return raw;
};

const normalizeHistoryFieldKey = (field) => {
   const raw = String(field || "").trim().toLowerCase();
   if (!raw) return "";

   const compact = raw.replace(/[^a-z0-9]/g, "");
   if (
      compact.endsWith("starttime") ||
      compact.endsWith("start") ||
      compact.endsWith("datetime") ||
      compact === "date"
   ) {
      return "startTime";
   }
   if (compact.endsWith("userid") || compact.endsWith("studentid")) {
      return "userId";
   }
   if (compact.endsWith("instructorid")) {
      return "instructorId";
   }
   if (compact.endsWith("sector")) return "sector";
   if (compact.endsWith("gearbox")) return "gearbox";
   if (
      compact.endsWith("privatemessage") ||
      compact.endsWith("privatenote") ||
      compact.endsWith("note")
   ) {
      return "privateMessage";
   }
   if (
      compact.endsWith("color") ||
      compact.endsWith("colortoken") ||
      compact.endsWith("eventcolor")
   ) {
      return "color";
   }
   if (compact.endsWith("isfavorite") || compact.endsWith("favorite")) {
      return "isFavorite";
   }
   if (compact.endsWith("isimportant") || compact.endsWith("important")) {
      return "isImportant";
   }
   if (
      compact.endsWith("iscancelled") ||
      compact.endsWith("iscanceled") ||
      compact.endsWith("cancelled") ||
      compact.endsWith("canceled") ||
      compact.endsWith("cancel")
   ) {
      return "isCancelled";
   }
   return "";
};

const parseJsonMaybe = (value) => {
   if (typeof value !== "string") return value;
   const text = value.trim();
   if (!text) return value;
   const startsLikeJson =
      (text.startsWith("{") && text.endsWith("}")) ||
      (text.startsWith("[") && text.endsWith("]"));
   if (!startsLikeJson) return value;
   try {
      return JSON.parse(text);
   } catch {
      return value;
   }
};

const coerceBoolean = (value) => {
   if (typeof value === "boolean") return value;
   if (typeof value === "number") return value !== 0;
   const text = String(value ?? "")
      .trim()
      .toLowerCase();
   if (!text) return false;
   if (["true", "1", "yes", "da"].includes(text)) return true;
   if (["false", "0", "no", "nu"].includes(text)) return false;
   return Boolean(text);
};

const firstNonEmptyString = (...values) => {
   for (const value of values) {
      if (value === undefined || value === null) continue;
      if (typeof value === "object") continue;
      const text = String(value).trim();
      if (text) return text;
   }
   return "";
};

const readColorFromCharacteristicValue = (value) => {
   if (!value) return "";

   if (Array.isArray(value)) {
      for (const entry of value) {
         const picked = firstNonEmptyString(
            entry?.color,
            entry?.colorToken,
            entry?.eventColor,
            entry?.value,
            entry?.label,
         );
         if (picked) return picked;
      }
      return "";
   }

   if (typeof value === "object") {
      return firstNonEmptyString(
         value?.color,
         value?.colorToken,
         value?.eventColor,
         value?.value,
         value?.label,
      );
   }

   return firstNonEmptyString(value);
};

const readReservationColorValue = (source) => {
   if (!source || typeof source !== "object") return "";

   const direct = firstNonEmptyString(
      source?.color,
      source?.colorToken,
      source?.eventColor,
      source?.ultraColor,
      source?.reservation?.color,
      source?.reservation?.colorToken,
      source?.reservation?.eventColor,
      source?.reservation?.ultraColor,
      source?.raw?.color,
      source?.raw?.colorToken,
      source?.raw?.eventColor,
      source?.raw?.ultraColor,
      source?.characteristic?.color,
      source?.characteristics?.color,
      source?.reservation?.characteristic?.color,
      source?.reservation?.characteristics?.color,
      source?.raw?.characteristic?.color,
      source?.raw?.characteristics?.color,
   );
   if (direct) return direct;

   const characteristicCandidates = [
      source?.characteristic,
      source?.characteristics,
      source?.reservation?.characteristic,
      source?.reservation?.characteristics,
      source?.raw?.characteristic,
      source?.raw?.characteristics,
   ];
   for (const candidate of characteristicCandidates) {
      const picked = readColorFromCharacteristicValue(candidate);
      if (picked) return picked;
   }

   return "";
};

const readReservationIsFavorite = (source) =>
   coerceBoolean(
      source?.isFavorite ??
         source?.is_favorite ??
         source?.favorite ??
         source?.reservation?.isFavorite ??
         source?.reservation?.is_favorite ??
         source?.reservation?.favorite ??
         source?.raw?.isFavorite ??
         source?.raw?.is_favorite ??
         source?.raw?.favorite ??
         false,
   );

const readReservationIsImportant = (source) =>
   coerceBoolean(
      source?.isImportant ??
         source?.is_important ??
         source?.important ??
         source?.reservation?.isImportant ??
         source?.reservation?.is_important ??
         source?.reservation?.important ??
         source?.raw?.isImportant ??
         source?.raw?.is_important ??
         source?.raw?.important ??
         false,
   );

const readReservationIsCancelled = (source) =>
   coerceBoolean(
      source?.isCancelled ??
         source?.is_cancelled ??
         source?.isCanceled ??
         source?.is_canceled ??
         source?.reservation?.isCancelled ??
         source?.reservation?.is_cancelled ??
         source?.reservation?.isCanceled ??
         source?.reservation?.is_canceled ??
         source?.raw?.isCancelled ??
         source?.raw?.is_cancelled ??
         source?.raw?.isCanceled ??
         source?.raw?.is_canceled ??
         false,
   );

const extractIdLike = (value) => {
   const parsed = parseJsonMaybe(value);
   if (parsed && typeof parsed === "object") {
      const id =
         parsed.id ??
         parsed.userId ??
         parsed.studentId ??
         parsed.instructorId ??
         parsed.value ??
         "";
      return String(id ?? "").trim();
   }
   return String(parsed ?? "").trim();
};

const normalizeHistoryFieldValue = (field, value) => {
   const parsed = parseJsonMaybe(value);

   switch (field) {
      case "startTime":
         return parsed == null ? "" : String(parsed).trim();
      case "userId":
      case "instructorId":
         return extractIdLike(parsed);
      case "isFavorite":
      case "isImportant":
      case "isCancelled": {
         let raw = parsed;
         if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            const candidate =
               raw.value ??
               raw.to ??
               raw.new ??
               raw.after ??
               raw.current ??
               raw.next ??
               raw.checked ??
               raw.enabled;
            if (candidate !== undefined) raw = candidate;
         }
         return coerceBoolean(raw);
      }
      case "gearbox": {
         const gearbox = String(parsed ?? "").trim();
         if (!gearbox) return "";
         const low = gearbox.toLowerCase();
         if (low === "automat") return "Automat";
         if (low === "manual") return "Manual";
         return gearbox;
      }
      default:
         if (parsed == null) return "";
         if (typeof parsed === "object") return historyRawValueToString(parsed);
         return String(parsed);
   }
};

const buildHistoryReservationBase = (existing, reservationSnapshot) => {
   const merged = mergeReservationRows(reservationSnapshot, existing) || {};
   const baseUser = merged?.user || merged?.student || {};
   const baseInstructor = merged?.instructor || {};

   return {
      startTime:
         merged?.startTime ??
         merged?.start ??
         merged?.start_time ??
         merged?.date ??
         "",
      userId: extractIdLike(
         merged?.userId ?? merged?.studentId ?? baseUser?.id ?? "",
      ),
      instructorId: extractIdLike(
         merged?.instructorId ??
            merged?.instructor_id ??
            baseInstructor?.id ??
            "",
      ),
      sector: String(merged?.sector ?? merged?.sectorName ?? "").trim(),
      gearbox: normalizeHistoryFieldValue("gearbox", merged?.gearbox ?? ""),
      privateMessage: String(
         merged?.privateMessage ?? merged?.private_message ?? merged?.note ?? "",
      ).trim(),
      color: readReservationColorValue(merged),
      isFavorite: readReservationIsFavorite(merged),
      isImportant: readReservationIsImportant(merged),
      isCancelled: readReservationIsCancelled(merged),
      userName: personName(baseUser),
      instructorName: personName(baseInstructor),
   };
};

const historyModificationTextFromItem = (h) => {
   if (!h || typeof h !== "object") return "-";

   const explicitTextKeys = ["modification", "message", "details", "description"];
   for (const key of explicitTextKeys) {
      const value = h[key];
      if (value !== undefined && value !== null && value !== "") {
         return historyRawValueToString(value);
      }
   }

   if (h.changedFields !== undefined) {
      return historyRawValueToString(h.changedFields);
   }

   if (h.changes !== undefined) {
      return historyRawValueToString(h.changes);
   }

   const raw = String(h.action || h.status || h.type || "").trim();
   return raw || "-";
};

const historyChangesFromItem = (h) => {
   if (!h || typeof h !== "object") return [];

   if (
      h.changedFields &&
      typeof h.changedFields === "object" &&
      !Array.isArray(h.changedFields)
   ) {
      const rows = Object.entries(h.changedFields)
         .map(([field, diff]) => {
            const normalizedField = normalizeHistoryFieldKey(field);
            const rawField = String(field || "")
               .trim()
               .toLowerCase();
            if (
               rawField === "action" ||
               rawField === "newdata" ||
               rawField === "olddata" ||
               rawField === "metadata" ||
               rawField === "snapshot" ||
               rawField === "reservation" ||
               rawField === "data"
            ) {
               return null;
            }
            if (
               diff &&
               typeof diff === "object" &&
               ("from" in diff || "to" in diff)
            ) {
               const from = historyRawValueToString(diff.from);
               const to = historyRawValueToString(diff.to);
               if (from === to) return null;
               return {
                  field,
                  normalizedField,
                  from,
                  to,
                  fromRaw: diff.from,
                  toRaw: diff.to,
               };
            }

            if (diff && typeof diff === "object") {
               const fromCandidate =
                  diff.from ??
                  diff.old ??
                  diff.before ??
                  diff.previous ??
                  diff.prev ??
                  diff.oldValue ??
                  diff.fromValue;
               const toCandidate =
                  diff.to ??
                  diff.new ??
                  diff.after ??
                  diff.current ??
                  diff.next ??
                  diff.newValue ??
                  diff.toValue ??
                  diff.value;

               if (fromCandidate !== undefined || toCandidate !== undefined) {
                  const from = historyRawValueToString(fromCandidate);
                  const to = historyRawValueToString(toCandidate);
                  if (from === to) return null;
                  return {
                     field,
                     normalizedField,
                     from,
                     to,
                     fromRaw: fromCandidate,
                     toRaw: toCandidate,
                  };
               }
            }

            if (!normalizedField) return null;

            return {
               field,
               normalizedField,
               from: "",
               to: historyRawValueToString(diff),
               fromRaw: undefined,
               toRaw: diff,
            };
         })
         .filter(Boolean);

      if (rows.length) return rows;
   }

   if (Array.isArray(h.changes)) {
      const rows = h.changes
         .map((change) => {
            const field = change?.field || change?.path || "(câmp)";
            const normalizedField = normalizeHistoryFieldKey(field);
            if (!normalizedField) return null;
            const fromRaw =
               change?.from ??
               change?.old ??
               change?.before ??
               change?.previous ??
               change?.prev ??
               change?.oldValue ??
               change?.fromValue;
            const toRaw =
               change?.to !== undefined
                  ? change.to
                  : change?.value ??
                    change?.new ??
                    change?.after ??
                    change?.current ??
                    change?.next ??
                    change?.newValue ??
                    change?.toValue;
            const from = historyRawValueToString(fromRaw);
            const to = historyRawValueToString(
               toRaw,
            );
            if (from === to) return null;
            return {
               field,
               normalizedField,
               from,
               to,
               fromRaw,
               toRaw,
            };
         })
         .filter(Boolean);

      if (rows.length) return rows;
   }

   if (h.changedFields !== undefined || Array.isArray(h.changes)) {
      return [];
   }

   const fallback = historyModificationTextFromItem(h);
   if (!fallback || fallback === "-") return [];
   return [
      {
         field: "",
         normalizedField: "",
         from: "",
         to: fallback,
         fromRaw: undefined,
         toRaw: fallback,
      },
   ];
};

export default function ReservationEditPopup({
   reservationId,
   reservationSnapshot = null,
   initialView = "form",
   showTicketFlags = true,
   showColorControl = true,
   showPersonalNote = true,
   onClose,
}) {
   const dispatch = useDispatch();
   const { user } = useContext(UserContext) || {};
   const rootRef = useRef(null);

   const leftOnceRef = useRef(false);

   // ✅ FIX: guard anti-close dublu
   const closingRef = useRef(false);

   // ✅ pentru hydratare
   const didHydrate = useRef(false);
   const [alerts, setAlerts] = useState([]);
   const [mutationPending, setMutationPending] = useState(false);

   // ✅ reset “one-shot” când se schimbă rezervarea (în caz de reuse fără unmount complet)
   useEffect(() => {
      setMutationPending(false);
      closingRef.current = false;
      didHydrate.current = false;
      leftOnceRef.current = false;
   }, [reservationId]);

   const joinReservation = useCallback(() => {
      const rid = String(reservationId ?? "").trim();
      if (!rid) return;
      const ws = typeof window !== "undefined" ? window.__reservationWS : null;
      ws?.joinReservation?.(rid);
   }, [reservationId]);

   const leaveReservation = useCallback(() => {
      if (leftOnceRef.current) return;
      leftOnceRef.current = true;

      const rid = String(reservationId ?? "").trim();
      if (!rid) return;
      const ws = typeof window !== "undefined" ? window.__reservationWS : null;
      ws?.leaveReservation?.(rid);
   }, [reservationId]);

   // ✅ intri în edit => JOIN; ieși/unmount => LEAVE (garantat)
   useEffect(() => {
      leftOnceRef.current = false;
      joinReservation();
      return () => {
         leaveReservation();
      };
   }, [joinReservation, leaveReservation]);

   const closeSelf = useCallback(() => {
      if (closingRef.current) return; // ✅ nu mai închide de 2 ori
      closingRef.current = true;

      leaveReservation(); // ✅ instant + broadcast + redraw

      if (typeof onClose === "function") return onClose();

      try {
         closeSubPopupStore();
      } catch {}
      try {
         closePopupStore();
      } catch {}
   }, [onClose, leaveReservation]);

   useEffect(() => {
      const onKeyDown = (e) => {
         if (mutationPending) return;
         if (e.key === "Escape") closeSelf();
      };

      document.addEventListener("keydown", onKeyDown, true);

      return () => {
         document.removeEventListener("keydown", onKeyDown, true);
      };
   }, [closeSelf, mutationPending]);

   const pushAlert = (type, text) =>
      setAlerts((prev) => [...prev, { id: Date.now(), type, text }]);
   const popAlert = () => setAlerts((prev) => prev.slice(0, -1));

   const reservations = useSelector((s) => s.reservations?.list || []);
   const studentsAll = useSelector((s) => s.students?.list || []);
   const instructors = useSelector((s) => s.instructors?.list || []);
   const usersById = useMemo(() => getUserByIdMap(studentsAll), [studentsAll]);
   const instructorsById = useMemo(() => {
      const map = new Map();
      for (const instructor of instructors || []) {
         if (instructor?.id == null) continue;
         map.set(String(instructor.id), instructor);
      }
      return map;
   }, [instructors]);

   useEffect(() => {
      if (!reservations?.length) dispatch(fetchReservationsDelta());
   }, [dispatch, reservations?.length]);

   const existingFromStore = useMemo(
      () => getReservationById(reservations, reservationId),
      [reservations, reservationId],
   );
   const existing = useMemo(
      () => mergeReservationRows(reservationSnapshot, existingFromStore),
      [existingFromStore, reservationSnapshot],
   );
   const existingReservationId = useMemo(() => {
      const fromExisting = getReservationEntityId(existing);
      if (fromExisting != null) return String(fromExisting).trim();
      return String(reservationId ?? "").trim();
   }, [existing, reservationId]);

   const hasUserRole = (u) => {
      const role = String(
         u?.role ?? u?.Role ?? u?.userRole ?? "",
      ).toUpperCase();
      if (role === "USER") return true;
      const roles = Array.isArray(u?.roles)
         ? u.roles.map((r) => String(r).toUpperCase())
         : [];
      return roles.includes("USER");
   };

   const [selectedDate, setSelectedDate] = useState(() => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
   });
   const [selectedTime, setSelectedTime] = useState(null);

   const [sector, setSector] = useState("Botanica");
   const districts = useDistricts();
   const [gearbox, setGearbox] = useState("Manual");

   const [studentId, setStudentId] = useState("");
   const [instructorId, setInstructorId] = useState("");

   const [privateMessage, setPrivateMessage] = useState("");
   const [colorToken, setColorToken] = useState("--event-default");

   const [isFavorite, setIsFavorite] = useState(false);
   const [isImportant, setIsImportant] = useState(false);
   const [isCancelled, setIsCanceled] = useState(false);

   const [colorHoverText, setColorHoverText] = useState("");
   const colorHoverTimerRef = useRef(null);
   useEffect(() => {
      return () => {
         if (colorHoverTimerRef.current)
            clearTimeout(colorHoverTimerRef.current);
      };
   }, []);

   useEffect(() => {
      if (!existing || didHydrate.current) return;

      const lk = existing.startTime
         ? busyLocalKeyFromStored(existing.startTime)
         : localKeyFromTs(Date.now(), MOLDOVA_TZ);

      const [dayStr, hhmm] = lk.split("|");
      setSelectedDate(localDateObjFromStr(dayStr));
      setSelectedTime(oreDisponibile.find((o) => o.oraStart === hhmm) || null);
      setSector(existing.sector || "Botanica");
      setGearbox(
         (existing.gearbox || "Manual").toLowerCase() === "automat"
            ? "Automat"
            : "Manual",
      );

      setStudentId(
         existing?.userId || existing?.studentId
            ? String(existing.userId || existing.studentId)
            : "",
      );
      const existingInstructorId = normalizeInstructorIdStrict(
         existing?.instructorId ??
            existing?.instructor_id ??
            existing?.instructor?.id,
      );
      setInstructorId(
         existingInstructorId != null ? String(existingInstructorId) : "",
      );

      setPrivateMessage(existing?.privateMessage || "");
      setColorToken(
         normalizeColorToken(
            readReservationColorValue(existing),
            "--event-default",
         ),
      );

      setIsFavorite(readReservationIsFavorite(existing));
      setIsImportant(readReservationIsImportant(existing));
      setIsCanceled(readReservationIsCancelled(existing));

      didHydrate.current = true;
   }, [existing]);

   const originalStudentId = useMemo(
      () =>
         existing?.userId || existing?.studentId
            ? String(existing.userId || existing.studentId)
            : "",
      [existing],
   );
   const originalInstructorId = useMemo(() => {
      const id = normalizeInstructorIdStrict(
         existing?.instructorId ??
            existing?.instructor_id ??
            existing?.instructor?.id,
      );
      return id != null ? String(id) : "";
   }, [existing]);

   const [view, setView] = useState(() => {
      return String(initialView || "")
         .trim()
         .toLowerCase() === "history"
         ? "history"
         : "form";
   }); // "form" | "studentSearch" | "instructorSearch" | "history"
   const [qStudent, setQStudent] = useState("");
   const [qInstructor, setQInstructor] = useState("");
   const [calendarReady, setCalendarReady] = useState(false);
   const [availabilityEditorOpen, setAvailabilityEditorOpen] = useState(false);
   const [availabilityRecalcTick, setAvailabilityRecalcTick] = useState(0);

   const students = useMemo(() => {
      if (view !== "studentSearch") return EMPTY_USERS;
      return (studentsAll || []).filter(hasUserRole);
   }, [studentsAll, view]);

   useEffect(() => {
      let raf = 0;
      raf = requestAnimationFrame(() => {
         setCalendarReady(true);
      });
      return () => {
         if (raf) cancelAnimationFrame(raf);
      };
   }, []);

   useEffect(() => {
      const nextView =
         String(initialView || "")
            .trim()
            .toLowerCase() === "history"
            ? "history"
            : "form";
      setView(nextView);
   }, [initialView, reservationId]);

   useEffect(() => {
      if (view === "studentSearch" && !studentsAll?.length) {
         dispatch(fetchStudents());
      }
      if (view === "instructorSearch" && !instructors?.length) {
         dispatch(fetchInstructors());
      }
   }, [view, studentsAll?.length, instructors?.length, dispatch]);

   const filteredStudents = useMemo(() => {
      if (view !== "studentSearch") return EMPTY_USERS;
      const q = (qStudent || "").trim().toLowerCase();
      if (!q) return (students || []).slice(0, SEARCH_RESULTS_LIMIT);
      return (students || [])
         .filter((s) => {
            const full =
               `${s.firstName || ""} ${s.lastName || ""}`.toLowerCase();
            const phone = (s.phone || "").toLowerCase();
            const email = (s.email || "").toLowerCase();
            return full.includes(q) || phone.includes(q) || email.includes(q);
         })
         .slice(0, SEARCH_RESULTS_LIMIT);
   }, [students, qStudent, view]);

   // Instructorul deja atribuit rămâne în listă chiar dacă nu poate avea
   // coloană, altfel o lecție ajunsă pe un instructor de schimb nu ar mai putea
   // fi mutată de acolo.
   const assignableInstructors = useMemo(() => {
      const currentId = String(instructorId || "").trim();
      return (instructors || []).filter(
         (i) =>
            canOwnCalendarReservations(i) ||
            (currentId && String(i?.id ?? "").trim() === currentId),
      );
   }, [instructors, instructorId]);

   const filteredInstructors = useMemo(() => {
      if (view !== "instructorSearch") return EMPTY_USERS;
      const q = (qInstructor || "").trim().toLowerCase();
      if (!q) return assignableInstructors.slice(0, SEARCH_RESULTS_LIMIT);
      return assignableInstructors
         .filter((i) => {
            const full =
               `${i.firstName || ""} ${i.lastName || ""}`.toLowerCase();
            const phone = (i.phone || "").toLowerCase();
            return full.includes(q) || phone.includes(q);
         })
         .slice(0, SEARCH_RESULTS_LIMIT);
   }, [assignableInstructors, qInstructor, view]);

   const [freeSlots, setFreeSlots] = useState([]);
   const [freeSlotsLoading, setFreeSlotsLoading] = useState(false);
   const canEditPastReservations = useMemo(
      () => isAdminOrManager(user),
      [user],
   );
   const availabilityWindowStart = useMemo(() => {
      if (!canEditPastReservations) return new Date();

      const anchor =
         selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
            ? selectedDate
            : existing?.startTime
              ? new Date(existing.startTime)
              : new Date();

      const start = new Date(anchor);
      start.setHours(0, 0, 0, 0);
      start.setDate(
         start.getDate() - ADMIN_MANAGER_AVAILABILITY_BACK_OFFSET_DAYS,
      );
      return start;
   }, [canEditPastReservations, existing?.startTime, selectedDate]);
   const availabilityWindowDays = canEditPastReservations
      ? ADMIN_MANAGER_AVAILABILITY_DAYS
      : DEFAULT_AVAILABILITY_DAYS;
   const fullGrid = useMemo(() => {
      if (!availabilityEditorOpen) return [];
      return buildFullGridSlots(
         availabilityWindowDays,
         availabilityWindowStart,
      );
   }, [
      availabilityEditorOpen,
      availabilityWindowDays,
      availabilityWindowStart,
   ]);
   const freeLocalKeySet = useMemo(
      () => new Set(freeSlots.map((iso) => localKeyForIso(iso))),
      [freeSlots],
   );
   const reservationsList = Array.isArray(reservations)
      ? reservations
      : EMPTY_RESERVATIONS;
   const availabilityPairKey = useMemo(() => {
      const sid = String(studentId ?? "").trim();
      const iid = String(instructorId ?? "").trim();
      if (!sid || !iid) return "";
      return `${String(reservationId ?? "")}|${sid}|${iid}`;
   }, [reservationId, studentId, instructorId]);

   useEffect(() => {
      if (!availabilityEditorOpen || !availabilityPairKey) {
         setFreeSlots([]);
         setFreeSlotsLoading(false);
         return;
      }

      const studentIdStr = String(studentId ?? "").trim();
      const instructorIdStr = String(instructorId ?? "").trim();
      const reservationIdStr = String(reservationId ?? "");

      const cached = readPairAvailabilityCache(
         reservationsList,
         availabilityPairKey,
      );
      if (cached) {
         const freeFromCache = buildFreeSlotsFromBusySets(
            fullGrid,
            cached.studentBusySet || EMPTY_KEY_SET,
            cached.instructorBusySet || EMPTY_KEY_SET,
         );
         setFreeSlots(freeFromCache);
         setFreeSlotsLoading(false);
         return;
      }

      let cancelled = false;
      let idleId = null;
      let timeoutId = 0;
      let idx = 0;

      const studentBusySet = new Set();
      const instructorBusySet = new Set();

      setFreeSlots([]);
      setFreeSlotsLoading(true);

      const finalize = () => {
         if (cancelled) return;
         writePairAvailabilityCache(
            reservationsList,
            availabilityPairKey,
            studentBusySet,
            instructorBusySet,
         );
         const free = buildFreeSlotsFromBusySets(
            fullGrid,
            studentBusySet,
            instructorBusySet,
         );
         setFreeSlots(free);
         setFreeSlotsLoading(false);
      };

      const scheduleNext = () => {
         if (cancelled) return;
         if (
            typeof window !== "undefined" &&
            typeof window.requestIdleCallback === "function"
         ) {
            idleId = window.requestIdleCallback(processChunk, { timeout: 32 });
            return;
         }
         timeoutId = window.setTimeout(() => processChunk(), 0);
      };

      const processChunk = (deadline) => {
         if (cancelled) return;

         let processed = 0;
         const budgeted =
            !!deadline && typeof deadline.timeRemaining === "function";

         while (idx < reservationsList.length) {
            if (budgeted && processed >= 200 && deadline.timeRemaining() <= 2) {
               break;
            }

            const r = reservationsList[idx++];
            processed += 1;

            if (String(getReservationEntityId(r) ?? "") === reservationIdStr)
               continue;

            const sid = String(r?.userId ?? r?.studentId ?? "").trim();
            const iid = String(r?.instructorId ?? "").trim();
            if (sid !== studentIdStr && iid !== instructorIdStr) continue;

            const st = getStartFromReservation(r);
            if (!st) continue;

            const key = getCachedBusyLocalKey(st);
            if (!key) continue;

            if (sid === studentIdStr) studentBusySet.add(key);
            if (iid === instructorIdStr) instructorBusySet.add(key);
         }

         if (idx >= reservationsList.length) {
            finalize();
            return;
         }

         scheduleNext();
      };

      scheduleNext();

      return () => {
         cancelled = true;
         if (
            idleId != null &&
            typeof window !== "undefined" &&
            typeof window.cancelIdleCallback === "function"
         ) {
            window.cancelIdleCallback(idleId);
         }
         if (timeoutId) clearTimeout(timeoutId);
      };
   }, [
      availabilityEditorOpen,
      availabilityRecalcTick,
      availabilityPairKey,
      fullGrid,
      instructorId,
      reservationId,
      reservationsList,
      studentId,
   ]);

   const freeByDay = useMemo(() => {
      const map = new Map();
      for (const iso of freeSlots) {
         const key = localKeyForIso(iso);
         const day = key.split("|")[0];
         map.set(day, (map.get(day) || 0) + 1);
      }
      return map;
   }, [freeSlots]);

   const selectedStudent = useMemo(() => {
      if (!studentId) return null;

      const studentIdStr = String(studentId);
      const snapshotStudent =
         existing?.user ??
         existing?.student ??
         reservationSnapshot?.user ??
         reservationSnapshot?.student ??
         null;
      const snapshotStudentId = String(
         snapshotStudent?.id ??
            snapshotStudent?.userId ??
            snapshotStudent?.studentId ??
            "",
      ).trim();

      if (snapshotStudent && snapshotStudentId === studentIdStr) {
         return snapshotStudent;
      }

      return usersById.get(studentIdStr) || null;
   }, [existing, reservationSnapshot, studentId, usersById]);
   const selectedInstructor = useMemo(() => {
      if (!instructorId) return null;

      const instructorIdStr = String(instructorId);
      const snapshotInstructor =
         existing?.instructor ?? reservationSnapshot?.instructor ?? null;
      const snapshotInstructorId = String(
         snapshotInstructor?.id ?? snapshotInstructor?.instructorId ?? "",
      ).trim();

      if (snapshotInstructor && snapshotInstructorId === instructorIdStr) {
         return snapshotInstructor;
      }

      return (
         (instructors || []).find((i) => String(i.id) === instructorIdStr) ||
         null
      );
   }, [existing, instructors, instructorId, reservationSnapshot]);

   const refreshAffectedStudentReservations = useCallback(
      async (...studentIds) => {
         const uniqueIds = Array.from(
            new Set(
               studentIds
                  .map((value) => String(value ?? "").trim())
                  .filter(Boolean),
            ),
         );
         if (!uniqueIds.length) return;

         await Promise.all(
            uniqueIds.map(async (sid) => {
               try {
                  await (dispatch(fetchUserReservations(sid)).unwrap?.() ??
                     dispatch(fetchUserReservations(sid)));
               } catch (error) {}
            }),
         );
      },
      [dispatch],
   );

   const studentDisplay = selectedStudent
      ? `${selectedStudent.firstName || ""} ${
           selectedStudent.lastName || ""
        }`.trim()
      : "(neales)";
   const studentPhone =
      selectedStudent?.phone ||
      selectedStudent?.phoneNumber ||
      selectedStudent?.mobile ||
      selectedStudent?.telefon ||
      "";
   const instructorDisplay = selectedInstructor
      ? `${selectedInstructor.firstName || ""} ${
           selectedInstructor.lastName || ""
        }`.trim()
      : "(neales)";
   const instructorPhone = selectedInstructor?.phone || "";
   const studentNameCopyValue = studentDisplay !== "(neales)" ? studentDisplay : "";
   const studentPhoneCopyValue = String(studentPhone || "").trim();
   const handleCopyField = async (rawValue, label) => {
      const value = String(rawValue || "").trim();
      if (!value) return;
      const copied = await copyTextToClipboardSafe(value);
      if (copied) {
         pushAlert("success", `${label} copiat.`);
      } else {
         pushAlert("error", `Nu am putut copia ${label}.`);
      }
   };

   const handleSectorToggle = useCallback(() => {
      // trece prin sectoarele active (lista din admin)
      const order = activeDistrictNames(districts);
      setSector((prev) => {
         const current = districtKey(prev);
         const idx = order.findIndex((name) => districtKey(name) === current);
         if (idx === -1) return order[0];
         return order[(idx + 1) % order.length];
      });
   }, [districts]);

   const handleGearboxToggle = useCallback(() => {
      setGearbox((prev) => {
         const current = String(prev || "").toLowerCase();
         if (current === "automat") return "Manual";
         return "Automat";
      });
   }, []);

   const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
   const AFTER_DELETE_DELAY_MS = 800;

   const onDelete = async () => {
      if (!existing || !existingReservationId || mutationPending) return;
      const ok = window.confirm("Ștergi această rezervare?");
      if (!ok) return;

      triggerCalendarRefresh({
         type: "reservation-deleted",
         reservationId: String(existingReservationId),
      });

      setMutationPending(true);
      try {
         await (dispatch(removeReservation(existingReservationId)).unwrap?.() ??
            dispatch(removeReservation(existingReservationId)));
         await refreshAffectedStudentReservations(
            originalStudentId || studentId,
         );
         closeSelf();
      } catch (e) {
         const detail = String(e?.message || "").trim();
         pushAlert("error", detail || "Nu am putut șterge această rezervare.");
      } finally {
         setMutationPending(false);
      }
   };

   const onSave = async () => {
      if (mutationPending) return;
      if (!instructorId) return pushAlert("error", "Selectează instructorul.");
      if (!studentId) return pushAlert("error", "Selectează elevul.");

      const instructorIdNum = normalizeInstructorIdStrict(instructorId);
      if (instructorIdNum == null) {
         return pushAlert(
            "error",
            "Instructor invalid. Selectează instructorul din listă.",
         );
      }

      const currentKey = existing?.startTime
         ? busyLocalKeyFromStored(existing.startTime)
         : null;
      const selectedKey =
         selectedDate && selectedTime
            ? localKeyForDateAndTime(selectedDate, selectedTime.oraStart)
            : null;

      const selectedIsoUTC =
         selectedDate && selectedTime
            ? toUtcIsoFromMoldova(selectedDate, selectedTime.oraStart)
            : null;

      const selectedIsoForBackend =
         selectedIsoUTC && BUSY_KEYS_MODE === "local-match"
            ? isoForDbMatchLocalHour(selectedIsoUTC)
            : selectedIsoUTC;
      const selectedStartTimeForBackend = selectedIsoUTC
         ? BUSY_KEYS_MODE === "local-match"
            ? selectedIsoForBackend
            : selectedIsoUTC
         : null;
      const existingWasCancelled = readReservationIsCancelled(existing);
      const cancellationStateChanged = isCancelled !== existingWasCancelled;

      const changingTime = !!selectedKey && selectedKey !== currentKey;
      const changingStudent = String(studentId) !== String(originalStudentId);
      const changingInstructor =
         String(instructorId) !== String(originalInstructorId);

      if (changingTime && !selectedIsoUTC) {
         return pushAlert(
            "error",
            "Selectează data și ora pentru a modifica programarea.",
         );
      }

      const resolvedStartTime = resolveStartTimeForCancellationTransition({
         existingStartTime: existing?.startTime,
         selectedStartTime: selectedStartTimeForBackend,
         wasCancelled: existingWasCancelled,
         willBeCancelled: isCancelled,
         changingTime,
      });
      // Ora se trimite si cand doar regula de anulare a mutat-o (rezervare deja
      // anulata, ramasa fix pe slot) — altfel slotul ar ramane ocupat.
      const shouldUpdateStartTime =
         !!resolvedStartTime &&
         (changingTime ||
            cancellationStateChanged ||
            !isSameStoredStart(resolvedStartTime, existing?.startTime));

      const effectiveIsoForChecks =
         selectedIsoUTC ||
         (existing?.startTime
            ? new Date(existing.startTime).toISOString()
            : null);

      if (changingTime) {
         const conflictI = hasInstructorConflict(
            reservations,
            instructorIdNum,
            effectiveIsoForChecks,
            existingReservationId,
         );
         const conflictS = hasStudentConflict(
            reservations,
            studentId,
            effectiveIsoForChecks,
            existingReservationId,
         );
         if (conflictI || conflictS) {
            return pushAlert(
               "error",
               "Slot indisponibil (aceeași oră locală pentru elev sau instructor).",
            );
         }
      } else if (changingInstructor) {
         const conflictI = hasInstructorConflict(
            reservations,
            instructorIdNum,
            effectiveIsoForChecks,
            existingReservationId,
         );
         if (conflictI) {
            return pushAlert(
               "error",
               "Instructorul ales are deja o rezervare la această oră.",
            );
         }
      }

      if (changingStudent) {
         const effectiveIsoToSend =
            resolvedStartTime || existing?.startTime || null;

         const forUserPayload = {
            userId: Number(studentId),
            instructorId: instructorIdNum,
            instructorsGroupId:
               existing?.instructorsGroupId ?? existing?.groupId ?? undefined,
            reservations: [
               {
                  startTime: effectiveIsoToSend,
                  sector,
                  gearbox,
                  privateMessage,
                  color: normalizeColorToken(colorToken, "--event-default"),
                  isFavorite,
                  isImportant,
                  isCancelled,
               },
            ],
         };

         setMutationPending(true);
         let deletedExisting = false;
         let restoredExisting = false;
         try {
            await (dispatch(
               removeReservation(existingReservationId),
            ).unwrap?.() ?? dispatch(removeReservation(existingReservationId)));
            deletedExisting = true;
            await sleep(AFTER_DELETE_DELAY_MS);
            const createResult =
               await createReservationsForUser(forUserPayload);

            try {
               await (dispatch(fetchReservationsDelta()).unwrap?.() ??
                  dispatch(fetchReservationsDelta()));
            } catch {}

            await refreshAffectedStudentReservations(
               originalStudentId,
               studentId,
            );

            const createdReservationId =
               createResult?.id ??
               createResult?.reservationId ??
               createResult?.reservation?.id ??
               null;

            triggerCalendarRefresh({
               type: "focus-reservation",
               reservationId:
                  createdReservationId != null
                     ? String(createdReservationId)
                     : undefined,
               oldStartTime: existing?.startTime
                  ? String(existing.startTime)
                  : null,
               newStartTime: resolvedStartTime
                  ? String(resolvedStartTime)
                  : existing?.startTime
                    ? String(existing.startTime)
                    : null,
               forceReload: false,
            });
            closeSelf();
         } catch (e) {
            if (deletedExisting) {
               try {
                  await restoreReservation(existingReservationId);
                  restoredExisting = true;
               } catch (restoreError) {}

               try {
                  await (dispatch(fetchReservationsDelta()).unwrap?.() ??
                     dispatch(fetchReservationsDelta()));
               } catch {}

               await refreshAffectedStudentReservations(
                  originalStudentId,
                  studentId,
               );
            }

            const detail = String(e?.message || "").trim();
            const fallback = deletedExisting
               ? restoredExisting
                  ? "Nu am putut reprograma elevul, dar rezervarea inițială a fost restaurată."
                  : "Nu am putut reprograma elevul și restaurarea automată a eșuat. Verifică manual calendarul."
               : "Nu am putut reprograma elevul.";
            pushAlert("error", detail ? `${fallback} (${detail})` : fallback);
         } finally {
            setMutationPending(false);
         }
         return;
      }

      const payload = {
         sector,
         gearbox,
         instructorId: instructorIdNum,
         userId: Number(originalStudentId),
         instructorsGroupId: null,
         privateMessage,
         color: normalizeColorToken(colorToken, "--event-default"),
         isFavorite,
         isImportant,
         isCancelled,
         ...(shouldUpdateStartTime ? { startTime: resolvedStartTime } : {}),
      };

      const focusPayload = {
         type: "focus-reservation",
         reservationId: existingReservationId,
         oldStartTime: existing?.startTime ? String(existing.startTime) : null,
         newStartTime: shouldUpdateStartTime
            ? String(resolvedStartTime)
            : existing?.startTime
              ? String(existing.startTime)
              : null,
         forceReload: false,
      };
      const shouldRequestCalendarFocus =
         shouldUpdateStartTime ||
         changingInstructor ||
         cancellationStateChanged;

      setMutationPending(true);
      const previousReservationSnapshot = mergeReservationRows(existing) || {};
      const optimisticReservation = mergeReservationRows(
         previousReservationSnapshot,
         payload,
      );

      dispatch(
         patchReservationLocal({
            id: existingReservationId,
            changes: {
               ...optimisticReservation,
               _optimistic: true,
               _optimisticPending: true,
               _localEchoPending: false,
               _localEchoUntil: null,
            },
         }),
      );
      applyReservationGridPatch({
         reservation: {
            ...previousReservationSnapshot,
            ...optimisticReservation,
            id: existingReservationId,
         },
         calendarStore: getCalendarStore(),
         context: {
            usersById,
         },
      });
      if (shouldRequestCalendarFocus) {
         triggerCalendarRefresh(focusPayload);
      }

      closeSelf();

      void (async () => {
         try {
            const savedReservation = await patchReservation(
               existingReservationId,
               payload,
            );
            const mergedReservation = mergeReservationRows(
               previousReservationSnapshot,
               savedReservation,
               payload,
            );

            dispatch(
               patchReservationLocal({
                  id: existingReservationId,
                  changes: {
                     ...mergedReservation,
                     _optimistic: false,
                     _optimisticPending: false,
                     _localEchoPending: true,
                     _localEchoUntil: Date.now() + 15000,
                  },
               }),
            );
            applyReservationGridPatch({
               reservation: {
                  ...previousReservationSnapshot,
                  ...mergedReservation,
                  id: existingReservationId,
               },
               calendarStore: getCalendarStore(),
               context: {
                  usersById,
               },
            });

            void refreshAffectedStudentReservations(
               originalStudentId || studentId,
            );
         } catch (e) {
            dispatch(
               patchReservationLocal({
                  id: existingReservationId,
                  changes: {
                     ...previousReservationSnapshot,
                     _optimistic: false,
                     _optimisticPending: false,
                     _localEchoPending: true,
                     _localEchoUntil: Date.now() + 8000,
                  },
               }),
            );
            applyReservationGridPatch({
               reservation: {
                  ...previousReservationSnapshot,
                  id: existingReservationId,
               },
               calendarStore: getCalendarStore(),
               context: {
                  usersById,
               },
            });
            try {
               await (dispatch(fetchReservationsDelta()).unwrap?.() ??
                  dispatch(fetchReservationsDelta()));
            } catch {}
            void refreshAffectedStudentReservations(
               originalStudentId || studentId,
            );
            const detail = String(e?.message || "").trim();
            const text = detail
               ? `Modificarea nu s-a aplicat (${detail}).`
               : "Modificarea nu s-a aplicat.";
            pushAlert("error", text);
         }
      })();
      return;
   };

   const [historyLoading, setHistoryLoading] = useState(false);
   const [historyError, setHistoryError] = useState("");
   const [historyItems, setHistoryItems] = useState(null);

   const loadHistory = useCallback(async () => {
      if (!reservationId) return;
      setHistoryLoading(true);
      setHistoryError("");
      try {
         const data = await getReservationHistory(reservationId);
         const list = Array.isArray(data) ? data : data?.items || [];
         list.sort(
            (a, b) =>
               new Date(b.createdAt || b.timestamp || 0) -
               new Date(a.createdAt || a.timestamp || 0),
         );
         setHistoryItems(list);
      } catch (e) {
         setHistoryError(e?.message || "Nu am putut încărca istoricul.");
         setHistoryItems([]);
      } finally {
         setHistoryLoading(false);
      }
   }, [reservationId]);

   useEffect(() => {
      if (view === "history" && historyItems === null) {
         loadHistory();
      }
   }, [view, historyItems, loadHistory]);

   const formattedHistory = useMemo(() => {
      const baseReservation = buildHistoryReservationBase(
         existing,
         reservationSnapshot,
      );

      const formatHistoryFieldValue = (field, value) => {
         switch (field) {
            case "startTime": {
               const raw = String(value ?? "").trim();
               if (!raw) return "—";
               const parsed = new Date(raw);
               if (Number.isNaN(parsed.getTime())) return raw;
               return fmtDateTimeRO(raw);
            }
            case "userId": {
               const id = String(value ?? "").trim();
               if (!id) return baseReservation.userName || "—";
               const student = usersById.get(id);
               if (student) return personName(student) || id;
               if (
                  id === String(baseReservation.userId || "").trim() &&
                  baseReservation.userName
               ) {
                  return baseReservation.userName;
               }
               return id;
            }
            case "instructorId": {
               const id = String(value ?? "").trim();
               if (!id) return baseReservation.instructorName || "—";
               const instructor = instructorsById.get(id);
               if (instructor) return personName(instructor) || id;
               if (
                  id === String(baseReservation.instructorId || "").trim() &&
                  baseReservation.instructorName
               ) {
                  return baseReservation.instructorName;
               }
               return id;
            }
            case "isFavorite":
            case "isImportant":
            case "isCancelled":
               return coerceBoolean(value) ? "Da" : "Nu";
            case "color": {
               const color = colorNameFromValue(value);
               return color || "—";
            }
            default: {
               const text = String(value ?? "").trim();
               return text || "—";
            }
         }
      };

      const debugFieldLabel = (fieldKey, rawField) => {
         switch (fieldKey) {
            case "startTime":
               return "startTime";
            case "userId":
               return "userId";
            case "instructorId":
               return "instructorId";
            case "sector":
               return "sector";
            case "gearbox":
               return "gearbox";
            case "privateMessage":
               return "privateMessage";
            case "color":
               return "color";
            case "isFavorite":
               return "isFavorite";
            case "isImportant":
               return "isImportant";
            case "isCancelled":
               return "isCancelled";
            default: {
               const raw = String(rawField || "").trim();
               return raw || "(field)";
            }
         }
      };

      const debugValueForField = (fieldKey, value) => {
         if (value === undefined || value === null || value === "") return "∅";
         if (
            fieldKey === "isFavorite" ||
            fieldKey === "isImportant" ||
            fieldKey === "isCancelled"
         ) {
            return String(coerceBoolean(value));
         }
         return historyRawValueToString(value);
      };

      const readSnapshotFromHistoryItem = (item) => {
         const sources = [
            item,
            item?.data?.reservation,
            item?.reservationSnapshot,
            item?.reservationData,
            item?.reservation,
            item?.data?.newData,
            item?.newData,
            item?.changedFields?.newData,
         ].filter((entry) => entry && typeof entry === "object");
         if (!sources.length) return {};

         const out = {};
         const setField = (field, rawValue) => {
            if (rawValue === undefined || rawValue === null || rawValue === "") return;
            out[field] = normalizeHistoryFieldValue(field, rawValue);
         };

         for (const src of sources) {
            setField(
               "startTime",
               src.startTime ?? src.start ?? src.start_time ?? src.startAt ?? src.date,
            );
            setField(
               "userId",
               src.userId ?? src.studentId ?? src.user?.id ?? src.student?.id,
            );
            setField(
               "instructorId",
               src.instructorId ?? src.instructor_id ?? src.instructor?.id,
            );
            setField("sector", src.sector ?? src.sectorName);
            setField("gearbox", src.gearbox);
            setField(
               "privateMessage",
               src.privateMessage ?? src.private_message ?? src.note,
            );
            setField("color", src.color ?? src.colorToken ?? src.eventColor);
            if (
               "isFavorite" in src ||
               "is_favorite" in src ||
               "favorite" in src
            ) {
               setField(
                  "isFavorite",
                  src.isFavorite ?? src.is_favorite ?? src.favorite,
               );
            }
            if (
               "isImportant" in src ||
               "is_important" in src ||
               "important" in src
            ) {
               setField(
                  "isImportant",
                  src.isImportant ?? src.is_important ?? src.important,
               );
            }
            if (
               "isCancelled" in src ||
               "is_cancelled" in src ||
               "isCanceled" in src ||
               "is_canceled" in src
            ) {
               setField(
                  "isCancelled",
                  src.isCancelled ??
                     src.is_cancelled ??
                     src.isCanceled ??
                     src.is_canceled,
               );
            }
         }

         return out;
      };

      const historySortTime = (item) => {
         const raw =
            item?.createdAt || item?.timestamp || item?.date || item?.updatedAt || 0;
         const ms = new Date(raw).getTime();
         return Number.isFinite(ms) ? ms : 0;
      };

      const historyAsc = (historyItems || [])
         .map((item, index) => ({ item, index }))
         .sort((a, b) => {
            const aTime = historySortTime(a.item);
            const bTime = historySortTime(b.item);

            if (aTime !== bTime) return aTime - bTime;
            return a.index - b.index;
         })
         .map((entry) => entry.item);

      if (!historyAsc.length) return [];

      const oldestSnapshot = readSnapshotFromHistoryItem(historyAsc[0]);
      const initialState = {
         ...baseReservation,
         ...oldestSnapshot,
      };

      for (let i = historyAsc.length - 1; i >= 0; i -= 1) {
         const modifications = historyChangesFromItem(historyAsc[i]);
         for (const change of modifications) {
            const fieldKey =
               change?.normalizedField || normalizeHistoryFieldKey(change?.field);
            if (!fieldKey) continue;

            if (change?.fromRaw !== undefined && change.fromRaw !== null) {
               initialState[fieldKey] = normalizeHistoryFieldValue(
                  fieldKey,
                  change.fromRaw,
               );
               continue;
            }

            if (change?.from !== undefined && change.from !== "") {
               initialState[fieldKey] = normalizeHistoryFieldValue(
                  fieldKey,
                  change.from,
               );
            }
         }
      }

      let cursorState = { ...initialState };

      const chronologicalEntries = historyAsc.map((h, idx) => {
         const when =
            h.timestamp ||
            h.date ||
            h.createdAt ||
            h.updatedAt ||
            existing?.startTime;

         const who = h.changedByUser
            ? `${h.changedByUser.firstName || ""} ${
                 h.changedByUser.lastName || ""
              }`.trim()
            : "";
         const modifications = historyChangesFromItem(h);
         const changedByField = new Map();
         for (const change of modifications) {
            const fieldKey =
               change?.normalizedField || normalizeHistoryFieldKey(change?.field);
            if (!fieldKey) continue;
            changedByField.set(fieldKey, change);
         }
         const explicitDebugChanges = modifications
            .map((change) => {
               const fieldKey =
                  change?.normalizedField || normalizeHistoryFieldKey(change?.field);
               if (!fieldKey) return null;
               const label = debugFieldLabel(fieldKey, change?.field);

               const fromValue =
                  change?.fromRaw !== undefined
                     ? debugValueForField(fieldKey, change.fromRaw)
                     : debugValueForField(fieldKey, change?.from);
               const toValue =
                  change?.toRaw !== undefined
                     ? debugValueForField(fieldKey, change.toRaw)
                     : debugValueForField(fieldKey, change?.to);

               const fromText = String(fromValue ?? "").trim() || "∅";
               const toText = String(toValue ?? "").trim() || "∅";
               if (fromText === toText) return null;
               return `${label}: from ${fromText} -> to ${toText}`;
            })
            .filter(Boolean);

         const previousState = { ...cursorState };
         const snapshotPatch = readSnapshotFromHistoryItem(h);
         let reservationView = { ...cursorState };
         if (Object.keys(snapshotPatch).length) {
            if (changedByField.size === 0) {
               reservationView = {
                  ...reservationView,
                  ...snapshotPatch,
               };
            } else {
               for (const [fieldKey, value] of Object.entries(snapshotPatch)) {
                  if (changedByField.has(fieldKey)) continue;
                  reservationView[fieldKey] = value;
               }
            }
         }

         for (const [fieldKey, change] of changedByField.entries()) {
            if (change?.toRaw !== undefined && change.toRaw !== null) {
               reservationView[fieldKey] = normalizeHistoryFieldValue(
                  fieldKey,
                  change.toRaw,
               );
               continue;
            }
            if (change?.to !== undefined && change.to !== "") {
               reservationView[fieldKey] = normalizeHistoryFieldValue(
                  fieldKey,
                  change.to,
               );
            }
         }

         const inferredDebugChanges = [
            "isFavorite",
            "isImportant",
            "isCancelled",
         ]
            .map((fieldKey) => {
               if (changedByField.has(fieldKey)) return null;
               const prevValue = normalizeHistoryFieldValue(
                  fieldKey,
                  previousState[fieldKey],
               );
               const nextValue = normalizeHistoryFieldValue(
                  fieldKey,
                  reservationView[fieldKey],
               );
               if (String(prevValue) === String(nextValue)) return null;
               return `${debugFieldLabel(fieldKey, fieldKey)} (inferat): from ${debugValueForField(fieldKey, prevValue)} -> to ${debugValueForField(fieldKey, nextValue)}`;
            })
            .filter(Boolean);

         const debugChanges = [...explicitDebugChanges, ...inferredDebugChanges];
         if (!debugChanges.length && String(h?.action || "").toUpperCase() === "CREATE") {
            debugChanges.push("action: created");
         }

         const cardStartRaw = String(reservationView.startTime || "").trim();
         const cardDate = fmtDateRO(cardStartRaw);
         const cardTime = fmtTimeRO(cardStartRaw);
         const cardStartFallback = formatHistoryFieldValue(
            "startTime",
            reservationView.startTime,
         );
         const dateDisplay = cardDate === "—" ? cardStartFallback : cardDate;
         const timeDisplay = cardDate === "—" ? "" : cardTime;

         const cardStudent = formatHistoryFieldValue("userId", reservationView.userId);
         const cardInstructor = formatHistoryFieldValue(
            "instructorId",
            reservationView.instructorId,
         );
         const cardSector = formatHistoryFieldValue("sector", reservationView.sector);
         const cardGearbox = formatHistoryFieldValue(
            "gearbox",
            reservationView.gearbox,
         );
         const cardNote = formatHistoryFieldValue(
            "privateMessage",
            reservationView.privateMessage,
         );

         const entry = {
            id: h.id || `${reservationId}-${idx}`,
            time: when ? fmtDateTimeRO(when) : "",
            by: who || "-",
            debugChanges,
            card: {
               date: dateDisplay,
               time: timeDisplay,
               student: cardStudent,
               instructor: cardInstructor,
               sector: cardSector,
               gearbox: cardGearbox,
               note: cardNote !== "—" ? cardNote : "",
               isFavorite: coerceBoolean(reservationView.isFavorite),
               isImportant: coerceBoolean(reservationView.isImportant),
               isCancelled: coerceBoolean(reservationView.isCancelled),
               colorCss: reservationColorToCss(reservationView.color),
               changed: {
                  startTime: changedByField.has("startTime"),
                  userId: changedByField.has("userId"),
                  instructorId: changedByField.has("instructorId"),
                  meta:
                     changedByField.has("sector") ||
                     changedByField.has("gearbox"),
                  isFavorite: changedByField.has("isFavorite"),
                  isImportant: changedByField.has("isImportant"),
                  isCancelled: changedByField.has("isCancelled"),
                  privateMessage: changedByField.has("privateMessage"),
                  color: changedByField.has("color"),
               },
            },
         };

         cursorState = { ...reservationView };
         return entry;
      });

      return chronologicalEntries.slice().reverse();
   }, [
      historyItems,
      existing,
      reservationId,
      reservationSnapshot,
      usersById,
      instructorsById,
   ]);

   if (!existing) {
      return (
         <div className="editReservationPopup">
            <div className="editReservationPopup__header">
               <div className="editReservationPopup__headerMain">
                  <h3 className="editReservationPopup__title">
                     Editează rezervarea
                  </h3>
               </div>
               <div className="editReservationPopup__headerActions" />
            </div>
            <div className="editReservationPopup__content">
               <div className="editReservationPopup__disclaimer">
                  Se încarcă datele...
               </div>
            </div>
         </div>
      );
   }

   const existingDayKey = existing?.startTime
      ? busyLocalKeyFromStored(existing.startTime).split("|")[0]
      : null;

   const filterDate = (date) => {
      const key = localDateStr(date);
      if (existingDayKey && key === existingDayKey) return true;
      return true;
   };

   const renderHistoryList = () => (
      <div className="editReservationPopup__history">
         <div className="editReservationPopup__history-header">
            <button
               className="editReservationPopup__btn editReservationPopup__btn--normal"
               onClick={() => setView("form")}
            >
               Înapoi
            </button>
            <button
               className="editReservationPopup__btn editReservationPopup__btn--normal"
               onClick={loadHistory}
               disabled={historyLoading}
               title="Reîncarcă istoricul"
            >
               {historyLoading ? "Se încarcă…" : "Reîncarcă"}
            </button>
         </div>

         <div className="editReservationPopup__history-grid-wrapper">
            <div className="editReservationPopup__history-grid">
               {historyError ? (
                  <div className="editReservationPopup__history-placeholder">
                     {historyError}
                  </div>
               ) : historyLoading ? (
                  <div className="editReservationPopup__history-placeholder">
                     Se încarcă istoricul…
                  </div>
               ) : (formattedHistory || []).length === 0 ? (
                  <div className="editReservationPopup__history-placeholder">
                     Nu există modificări pentru această rezervare.
                  </div>
               ) : (
                  formattedHistory.map((entry, index) => {
                     return (
                        <div
                           key={entry.id + "-" + index}
                           className="editReservationPopup__history-item"
                        >
                           <div className="editReservationPopup__history-meta">
                              <span className="editReservationPopup__history-meta-label">
                                 Data / ora
                              </span>
                              <span
                                 className={[
                                    "editReservationPopup__history-meta-value",
                                    entry.card?.changed?.startTime
                                       ? "is-changed"
                                       : "",
                                 ]
                                    .filter(Boolean)
                                    .join(" ")}
                              >
                                 {entry.time || "-"}
                              </span>

                              <span className="editReservationPopup__history-meta-label">
                                 Modificat de
                              </span>
                              <span className="editReservationPopup__history-meta-value">
                                 {entry.by || "-"}
                              </span>
                           </div>

                           <article
                              className={[
                                 "studentsProfileUI__reservationCard",
                                 "editReservationPopup__history-reservation-card",
                                 entry.card?.changed?.color ? "is-changed" : "",
                              ]
                                 .filter(Boolean)
                                 .join(" ")}
                              style={{
                                 background:
                                    entry.card?.colorCss || "var(--event-default)",
                              }}
                           >
                              {entry.card?.isCancelled ? (
                                 <span className="studentsProfileUI__reservationDot" />
                              ) : null}

                              <p
                                 className={[
                                    "studentsProfileUI__reservationDate",
                                    "editReservationPopup__history-res-line",
                                    entry.card?.changed?.startTime
                                       ? "is-changed"
                                       : "",
                                 ]
                                    .filter(Boolean)
                                    .join(" ")}
                              >
                                 {entry.card?.date || "—"}
                                 {entry.card?.time ? ", " : ""}
                                 {entry.card?.time ? (
                                    <span className="studentsProfileUI__reservationTime">
                                       {entry.card.time}
                                    </span>
                                 ) : null}
                              </p>

                              <p
                                 className={[
                                    "studentsProfileUI__reservationMeta",
                                    "studentsProfileUI__reservationMeta--sub",
                                    "editReservationPopup__history-res-line",
                                    entry.card?.changed?.userId ||
                                    entry.card?.changed?.instructorId
                                       ? "is-changed"
                                       : "",
                                 ]
                                    .filter(Boolean)
                                    .join(" ")}
                              >
                                 Cu{" "}
                                 {entry.card?.student ||
                                    entry.card?.instructor ||
                                    "-"}
                              </p>

                              <p
                                 className={[
                                    "studentsProfileUI__reservationMeta",
                                    "editReservationPopup__history-res-line",
                                    entry.card?.changed?.meta ? "is-changed" : "",
                                 ]
                                    .filter(Boolean)
                                    .join(" ")}
                              >
                                 {entry.card?.sector || "—"} ·{" "}
                                 {entry.card?.gearbox || "—"}
                                 {entry.card?.isFavorite ? (
                                    <span className="studentsProfileUI__reservationFlag">
                                       {" "}
                                       ⁂
                                    </span>
                                 ) : null}
                                 {entry.card?.isImportant ? (
                                    <span className="studentsProfileUI__reservationFlag">
                                       {" "}
                                       ‼
                                    </span>
                                 ) : null}
                              </p>

                              {entry.card?.note ? (
                                 <p
                                    className={[
                                       "studentsProfileUI__reservationMeta",
                                       "editReservationPopup__history-res-line",
                                       entry.card?.changed?.privateMessage
                                          ? "is-changed"
                                          : "",
                                    ]
                                       .filter(Boolean)
                                       .join(" ")}
                                 >
                                    {entry.card.note}
                                 </p>
                              ) : null}
                           </article>
                        </div>
                     );
                  })
               )}
            </div>
         </div>
      </div>
   );

   const renderStudentSearch = () => (
      <div className="editReservationPopup__group-body">
         <div className="editReservationPopup__search-header">
            <input
               type="text"
               className="editReservationPopup__search-input"
               placeholder="Caută elev"
               value={qStudent}
               onChange={(e) => setQStudent(e.target.value)}
            />
            <button
               className="editReservationPopup__btn editReservationPopup__btn--normal"
               onClick={() => setView("form")}
            >
               Înapoi
            </button>
         </div>

         <div className="editReservationPopup__search-list-wrapper">
            <ul className="editReservationPopup__search-list">
               {filteredStudents.map((s) => {
                  const full = `${s.firstName || ""} ${
                     s.lastName || ""
                  }`.trim();
                  const phone = s.phone || "";
                  const email = s.email || "";
                  return (
                     <li
                        key={s.id}
                        className="editReservationPopup__search-item"
                        onClick={() => {
                           setStudentId(String(s.id));
                           setView("form");
                        }}
                     >
                        <h3>{highlightText(full, qStudent)}</h3>
                        {phone && <p>{highlightText(phone, qStudent)}</p>}
                     </li>
                  );
               })}
            </ul>
         </div>
      </div>
   );

   const renderInstructorSearch = () => (
      <div className="editReservationPopup__group-body">
         <div className="editReservationPopup__search-header">
            <input
               type="text"
               className="editReservationPopup__search-input"
               placeholder="Caută instructor "
               value={qInstructor}
               onChange={(e) => setQInstructor(e.target.value)}
            />
            <button
               className="editReservationPopup__btn editReservationPopup__btn--normal"
               onClick={() => setView("form")}
            >
               Înapoi
            </button>
         </div>

         <div className="editReservationPopup__search-list-wrapper">
            <ul className="editReservationPopup__search-list">
               {filteredInstructors.map((i) => {
                  const full = `${i.firstName || ""} ${i.lastName || ""}`.trim();
                  const phone = i.phone || "";
                  return (
                     <li
                        key={i.id}
                        className="editReservationPopup__search-item"
                        onClick={() => {
                           setInstructorId(String(i.id));
                           setView("form");
                        }}
                     >
                        <h3>{highlightText(full, qInstructor)}</h3>
                        {phone && <p>{highlightText(phone, qInstructor)}</p>}
                     </li>
                  );
               })}
            </ul>
         </div>
      </div>
   );

   const renderForm = () => {
      const nowDayLocal = localDateStrTZ(new Date(), MOLDOVA_TZ);
      const nowHHMM = nowHHMMInMoldova();
      const currentKey = existing?.startTime
         ? busyLocalKeyFromStored(existing.startTime)
         : null;
      const currentDayKey = currentKey ? currentKey.split("|")[0] : null;
      const selectedDayLabel = selectedDate
         ? selectedDate.toLocaleDateString("ro-RO", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
           })
         : "Nesetată";
      const selectedTimeLabel = selectedTime?.eticheta || "Nesetată";
      const hasOptionsSection =
         showPersonalNote || showTicketFlags || showColorControl;

      return (
         <>
            <div className="editReservationPopup__group-body">
               <div className="editReservationPopup__form-row editReservationPopup__form-row--compact">
                  <div className="editReservationPopup__field editReservationPopup__field--grow-1">
                     <span className="editReservationPopup__field-label">
                        Ziua
                     </span>
                     <div className="editReservationPopup__field-line">
                        <span className="editReservationPopup__field-text">
                           {selectedDayLabel}
                        </span>
                     </div>
                  </div>
                  <div className="editReservationPopup__field editReservationPopup__field--grow-1">
                     <span className="editReservationPopup__field-label">
                        Ora
                     </span>
                     <div className="editReservationPopup__field-line">
                        <span className="editReservationPopup__field-text">
                           {selectedTimeLabel}
                        </span>
                     </div>
                  </div>
               </div>

               {availabilityEditorOpen && (
                  <div className="editReservationPopup__selector">
                     <div className="editReservationPopup__field editReservationPopup__field--calendar">
                        <h3 className="editReservationPopup__field-label">
                           Selectează data:
                        </h3>
                        {calendarReady ? (
                           <DatePicker
                              selected={selectedDate}
                              onChange={(d) => {
                                 setSelectedDate(d);
                                 setSelectedTime(null);
                              }}
                              inline
                              locale="ro"
                              openToDate={
                                 selectedDate ||
                                 (existing?.startTime
                                    ? new Date(existing.startTime)
                                    : undefined)
                              }
                              formatWeekDay={(name) =>
                                 name
                                    .substring(0, 2)
                                    .replace(/^./, (c) => c.toUpperCase())
                              }
                              filterDate={filterDate}
                              dayClassName={(date) => {
                                 if (canEditPastReservations) return "";
                                 const key = localDateStr(date);
                                 return freeByDay.has(key) ||
                                    key === currentDayKey
                                    ? ""
                                    : "editReservationPopup__day--inactive";
                              }}
                              calendarClassName="editReservationPopup__datepicker"
                           />
                        ) : (
                           <div
                              className="editReservationPopup__disclaimer"
                              style={{ minHeight: 300 }}
                           >
                              Se încarcă calendarul...
                           </div>
                        )}
                     </div>

                     <div className="editReservationPopup__field editReservationPopup__field--times">
                        <h3 className="editReservationPopup__field-label">
                           Selectează ora:
                        </h3>
                        <div className="editReservationPopup__times-list">
                           {!selectedDate && (
                              <div className="editReservationPopup__disclaimer">
                                 Te rog să selectezi mai întâi o zi!
                              </div>
                           )}
                           {selectedDate && freeSlotsLoading && (
                              <div className="editReservationPopup__disclaimer">
                                 Se calculează disponibilitatea...
                              </div>
                           )}

                           {oreDisponibile.map((ora) => {
                              const key =
                                 selectedDate && ora?.oraStart
                                    ? localKeyForDateAndTime(
                                         selectedDate,
                                         ora.oraStart,
                                      )
                                    : null;

                              const isSelected =
                                 selectedTime?.oraStart === ora.oraStart;

                              const isTodayLocal =
                                 selectedDate &&
                                 localDateStr(selectedDate) === nowDayLocal;
                              const pastToday =
                                 !canEditPastReservations &&
                                 isTodayLocal &&
                                 ora.oraStart <= nowHHMM;

                              const isExistingSlot =
                                 currentKey && key === currentKey;
                              const studentUnchanged =
                                 String(studentId) ===
                                 String(originalStudentId);

                              const available =
                                 (isExistingSlot && studentUnchanged) ||
                                 (key ? freeLocalKeySet.has(key) : false);

                              const disabled =
                                 !selectedDate || !available || pastToday;

                              return (
                                 <button
                                    key={ora.eticheta}
                                    onClick={() => setSelectedTime(ora)}
                                    disabled={disabled}
                                    className={[
                                       "editReservationPopup__time-btn",
                                       isSelected
                                          ? "editReservationPopup__time-btn--selected"
                                          : "",
                                       disabled
                                          ? "editReservationPopup__time-btn--disabled"
                                          : "",
                                       ora.eticheta === "19:30"
                                          ? "editReservationPopup__time-btn--wide"
                                          : "",
                                    ]
                                       .filter(Boolean)
                                       .join(" ")}
                                    title={
                                       !selectedDate
                                          ? "Alege o zi"
                                          : isExistingSlot && !studentUnchanged
                                            ? "Schimbi elevul: slotul actual trebuie să fie liber pentru elevul nou"
                                            : !available
                                              ? "Indisponibil (există altă rezervare la această oră)"
                                              : ""
                                    }
                                 >
                                    {ora.eticheta}
                                 </button>
                              );
                           })}
                        </div>
                     </div>
                  </div>
               )}
            </div>

            <div className="editReservationPopup__group-body">
               <div className="editReservationPopup__form-row editReservationPopup__form-row--spaced">
                  <div className="editReservationPopup__field editReservationPopup__field--clickable">
                     <span className="editReservationPopup__field-label">
                        Elev
                     </span>
                     <div
                        className="editReservationPopup__field-line"
                        onClick={() => setView("studentSearch")}
                     >
                        <span className="editReservationPopup__field-text">
                           {studentDisplay !== "(neales)"
                              ? studentDisplay
                              : "Alege elev"}
                        </span>
                        <button
                           type="button"
                           className="editReservationPopup__copy-btn"
                           title="Copiază numele elevului"
                           aria-label="Copiază numele elevului"
                           disabled={!studentNameCopyValue}
                           onClick={(event) => {
                              event.stopPropagation();
                              void handleCopyField(studentNameCopyValue, "Nume elev");
                           }}
                        >
                           <UIIcon
                              name="copy"
                              className="editReservationPopup__copy-icon"
                           />
                        </button>
                     </div>

                     <div
                        className="editReservationPopup__field-line"
                        onClick={() => setView("studentSearch")}
                     >
                        <span className="editReservationPopup__field-text">
                           {studentPhone || "Telefon elev"}
                        </span>
                        <button
                           type="button"
                           className="editReservationPopup__copy-btn"
                           title="Copiază telefonul elevului"
                           aria-label="Copiază telefonul elevului"
                           disabled={!studentPhoneCopyValue}
                           onClick={(event) => {
                              event.stopPropagation();
                              void handleCopyField(
                                 studentPhoneCopyValue,
                                 "Telefon elev",
                              );
                           }}
                        >
                           <UIIcon
                              name="copy"
                              className="editReservationPopup__copy-icon"
                           />
                        </button>
                     </div>
                  </div>

                  <div className="editReservationPopup__field editReservationPopup__field--clickable">
                     <span className="editReservationPopup__field-label">
                        Instructor
                     </span>
                     <div
                        className="editReservationPopup__field-line"
                        onClick={() => setView("instructorSearch")}
                     >
                        <span className="editReservationPopup__field-text">
                           {instructorDisplay !== "(neales)"
                              ? instructorDisplay
                              : "Alege instructor"}
                        </span>
                     </div>
                     <div
                        className="editReservationPopup__field-line"
                        onClick={() => setView("instructorSearch")}
                     >
                        <span className="editReservationPopup__field-text">
                           {instructorPhone || "Telefon instructor"}
                        </span>
                     </div>
                  </div>
               </div>

               <div className="editReservationPopup__form-row editReservationPopup__form-row--compact">
                  <div className="editReservationPopup__field editReservationPopup__field--clickable editReservationPopup__field--grow-1">
                     <span className="editReservationPopup__field-label">
                        Sector
                     </span>
                     <div
                        className="editReservationPopup__field-line"
                        onClick={handleSectorToggle}
                     >
                        <span className="editReservationPopup__field-text">
                           {sector || "Alege sector"}
                        </span>
                     </div>
                  </div>

                  <div className="editReservationPopup__field editReservationPopup__field--clickable editReservationPopup__field--grow-1">
                     <span className="editReservationPopup__field-label">
                        Cutie
                     </span>
                     <div
                        className="editReservationPopup__field-line"
                        onClick={handleGearboxToggle}
                     >
                        <span className="editReservationPopup__field-text">
                           {gearbox
                              ? gearbox.toLowerCase() === "automat"
                                 ? "Automat"
                                 : "Manual"
                              : "Alege cutie"}
                        </span>
                     </div>
                  </div>
               </div>
            </div>

            {hasOptionsSection && (
               <div className="editReservationPopup__group-body">
                  {(showPersonalNote || showTicketFlags) && (
                     <div className="editReservationPopup__form-row editReservationPopup__form-row--gap editReservationPopup__form-row--spaced">
                        {showPersonalNote && (
                           <div className="editReservationPopup__field">
                              <span className="editReservationPopup__field-label">
                                 Notiță
                              </span>
                              <textarea
                                 className="editReservationPopup__textarea"
                                 rows={2}
                                 placeholder="Notiță..."
                                 value={privateMessage}
                                 onChange={(e) =>
                                    setPrivateMessage(e.target.value)
                                 }
                              />
                           </div>
                        )}

                        {showTicketFlags && (
                           <div className="editReservationPopup__field">
                              <span className="editReservationPopup__field-label">
                                 Tichete
                              </span>
                              <div className="editReservationPopup__flag-grid">
                                 <button
                                    type="button"
                                    className={[
                                       "editReservationPopup__btn",
                                       "editReservationPopup__btn--flag",
                                       isFavorite
                                          ? "editReservationPopup__btn--active"
                                          : "",
                                    ]
                                       .filter(Boolean)
                                       .join(" ")}
                                    onClick={() => setIsFavorite((v) => !v)}
                                    title={
                                       isFavorite
                                          ? "Scoate din favorite"
                                          : "Marchează ca favorit"
                                    }
                                 >
                                    <UIIcon
                                       className="reservation-flag-icon react-icon"
                                       name="favorite"
                                    />
                                 </button>

                                 <button
                                    type="button"
                                    className={[
                                       "editReservationPopup__btn",
                                       "editReservationPopup__btn--flag",
                                       isImportant
                                          ? "editReservationPopup__btn--active"
                                          : "",
                                    ]
                                       .filter(Boolean)
                                       .join(" ")}
                                    onClick={() => setIsImportant((v) => !v)}
                                    title={
                                       isImportant
                                          ? "Scoate marcajul de important"
                                          : "Marchează ca important"
                                    }
                                 >
                                    <UIIcon
                                       className="reservation-flag-icon react-icon"
                                       name="important"
                                    />
                                 </button>

                                 <button
                                    type="button"
                                    className={[
                                       "editReservationPopup__btn",
                                       "editReservationPopup__btn--flag",
                                       isCancelled
                                          ? "editReservationPopup__btn--active"
                                          : "",
                                    ]
                                       .filter(Boolean)
                                       .join(" ")}
                                    onClick={() => setIsCanceled((v) => !v)}
                                    title={
                                       isCancelled
                                          ? "Scoate marcajul de anulat"
                                          : "Marchează rezervarea ca anulată"
                                    }
                                 >
                                    <UIIcon
                                       className="reservation-flag-icon react-icon"
                                       name="cancel"
                                    />
                                 </button>
                              </div>
                           </div>
                        )}
                     </div>
                  )}

                  {showColorControl && (
                     <div className="editReservationPopup__field editReservationPopup__field--stacked">
                        <span className="editReservationPopup__field-label">
                           Culoare
                        </span>

                        <div
                           className="editReservationPopup__color-grid"
                           role="radiogroup"
                           aria-label="Culoare eveniment"
                           style={COLOR_GRID_STYLE}
                        >
                           {COLOR_TOKENS.map((token) => {
                              const suffix = token.replace(/^--/, "");
                              const active = colorToken === token;
                              const name = COLOR_LABEL[suffix] || suffix;
                              const tip = COLOR_HINTS[suffix]
                                 ? `${COLOR_HINTS[suffix]}`
                                 : name;
                              return (
                                 <button
                                    key={token}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    aria-label={tip}
                                    title={tip}
                                    className={[
                                       "editReservationPopup__color-swatch",
                                       `editReservationPopup__color-swatch--${suffix}`,
                                       active ? "is-active" : "",
                                    ]
                                       .filter(Boolean)
                                       .join(" ")}
                                    style={calendarEventColorSwatchStyle(token)}
                                    onClick={() => setColorToken(token)}
                                    onFocus={() => setColorHoverText(tip)}
                                    onBlur={() => setColorHoverText("")}
                                    onTouchStart={() => {
                                       setColorHoverText(tip);
                                       if (colorHoverTimerRef.current)
                                          clearTimeout(
                                             colorHoverTimerRef.current,
                                          );
                                    }}
                                 />
                              );
                           })}
                        </div>
                        {colorHoverText && (
                           <div className="editReservationPopup__color-hint">
                              {colorHoverText}
                           </div>
                        )}
                     </div>
                  )}
               </div>
            )}

            <div className="editReservationPopup__group-body editReservationPopup__group-body--actions">
               <div className="editReservationPopup__btns editReservationPopup__btns--bottom">
                  <button
                     className="editReservationPopup__btn editReservationPopup__btn--delete"
                     onClick={onDelete}
                     disabled={mutationPending}
                  >
                     Șterge
                  </button>

                  <button
                     className="editReservationPopup__btn editReservationPopup__btn--edit"
                     onClick={() => setView("history")}
                     title="Vezi istoricul modificărilor"
                     disabled={mutationPending}
                  >
                     Istoric
                  </button>

                  <div className="editReservationPopup__btns-spacer" />

                  <button
                     className="editReservationPopup__btn editReservationPopup__btn--save"
                     onClick={onSave}
                     disabled={mutationPending}
                  >
                     Salvează
                  </button>
               </div>
            </div>
         </>
      );
   };

   const editReservationContextValue = {
      setView,
      loadHistory,
      historyLoading,
      historyError,
      formattedHistory,
      qStudent,
      setQStudent,
      filteredStudents,
      setStudentId,
      highlightText,
      qInstructor,
      setQInstructor,
      filteredInstructors,
      setInstructorId,
   };

   return (
      <EditReservationContext.Provider value={editReservationContextValue}>
      <div ref={rootRef} className="editReservationPopup">
         <div className="editReservationPopup__header">
            <div className="editReservationPopup__headerMain">
               <h3 className="editReservationPopup__title">
                  Editează rezervarea
               </h3>
            </div>
            <div className="editReservationPopup__headerActions">
               {!availabilityEditorOpen && (
                  <IconButton
                     className="editReservationPopup__iconBtn"
                     icon="edit"
                     variant="square"
                     onClick={() => {
                        setAvailabilityEditorOpen(true);
                        setAvailabilityRecalcTick((v) => v + 1);
                     }}
                     title="Editează data și ora"
                     aria-label="Editează data și ora"
                  />
               )}
            </div>
         </div>

         <div className="editReservationPopup__content">
            {view === "history" ? (
               <ReservationHistoryTab />
            ) : view === "studentSearch" ? (
               <ReservationStudentSearch />
            ) : view === "instructorSearch" ? (
               <ReservationInstructorSearch />
            ) : (
               renderForm()
            )}
         </div>

         <AlertPills messages={alerts} onDismiss={popAlert} />
      </div>
      </EditReservationContext.Provider>
   );
}
